import { useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useScroll } from 'framer-motion';

// 3D Tilt Card Component
export function TiltCard({ children, className = '', intensity = 15 }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [intensity, -intensity]), {
    stiffness: 300,
    damping: 30
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-intensity, intensity]), {
    stiffness: 300,
    damping: 30
  });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) / rect.width);
    y.set((e.clientY - centerY) / rect.height);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
        perspective: 1000
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Parallax Layer Component (Mouse based)
export function ParallaxLayer({ children, speed = 0.5, className = '' }) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const x = (e.clientX - window.innerWidth / 2) * speed * 0.02;
    const y = (e.clientY - window.innerHeight / 2) * speed * 0.02;
    setOffset({ x, y });
  };

  return (
    <motion.div
      className={className}
      onMouseMove={handleMouseMove}
      animate={{ x: offset.x, y: offset.y }}
      transition={{ type: 'spring', stiffness: 150, damping: 15 }}
    >
      {children}
    </motion.div>
  );
}

// Floating Animation Component
export function FloatingElement({ children, duration = 6, delay = 0, yOffset = 20, className = '' }) {
  return (
    <motion.div
      className={className}
      animate={{
        y: [0, -yOffset, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    >
      {children}
    </motion.div>
  );
}

// Glow Orb Component
export function GlowOrb({ color = 'royal', size = 300, className = '', blur = 100 }) {
  const colors = {
    cyan: 'rgba(6, 182, 212, 0.15)',
    purple: 'rgba(139, 92, 246, 0.12)',
    blue: 'rgba(59, 130, 246, 0.15)',
    pink: 'rgba(236, 72, 153, 0.12)',
    royal: 'rgba(30, 58, 138, 0.25)',
    midnight: 'rgba(13, 18, 36, 0.4)'
  };

  return (
    <motion.div
      className={`absolute rounded-full pointer-events-none ${className}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${colors[color] || colors.royal} 0%, transparent 70%)`,
        filter: `blur(${blur}px)`
      }}
      animate={{
        scale: [1, 1.15, 1],
        opacity: [0.6, 0.85, 0.6]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    />
  );
}

// 3D Grid Background
export function Grid3D({ className = '' }) {
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          transform: 'perspective(500px) rotateX(65deg)',
          transformOrigin: 'center top',
          maskImage: 'linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)'
        }}
      />
    </div>
  );
}

// Particle Field Component
export function ParticleField({ count = 50, className = '' }) {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5
  }));

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-blue-500/20 shadow-[0_0_8px_rgba(59,130,246,0.3)]"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0, 0.8, 0],
            scale: [0, 1, 0]
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      ))}
    </div>
  );
}

// Morphing Shape Component
export function MorphingShape({ className = '' }) {
  return (
    <motion.div
      className={`absolute ${className}`}
      style={{
        width: 400,
        height: 400,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(6, 182, 212, 0.12) 100%)',
        borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
        filter: 'blur(50px)'
      }}
      animate={{
        borderRadius: [
          '60% 40% 30% 70% / 60% 30% 70% 40%',
          '30% 60% 70% 40% / 50% 60% 30% 60%',
          '60% 40% 30% 70% / 60% 30% 70% 40%'
        ],
        rotate: [0, 180, 360]
      }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    />
  );
}

// 3D Rotating Cube
export function RotatingCube({ size = 100, className = '' }) {
  return (
    <div className={`relative ${className}`} style={{ width: size, height: size, perspective: 500 }}>
      <motion.div
        className="w-full h-full relative"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateX: 360, rotateY: 360 }}
        transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
      >
        {[
          { transform: `translateZ(${size/2}px)` },
          { transform: `rotateY(180deg) translateZ(${size/2}px)` },
          { transform: `rotateY(90deg) translateZ(${size/2}px)` },
          { transform: `rotateY(-90deg) translateZ(${size/2}px)` },
          { transform: `rotateX(90deg) translateZ(${size/2}px)` },
          { transform: `rotateX(-90deg) translateZ(${size/2}px)` }
        ].map((style, i) => (
          <div
            key={i}
            className="absolute inset-0 border border-blue-500/30 bg-blue-500/5 shadow-[inset_0_0_12px_rgba(59,130,246,0.1)]"
            style={{ ...style, backfaceVisibility: 'visible' }}
          />
        ))}
      </motion.div>
    </div>
  );
}

// Interactive Beam Effect
export function BeamEffect({ className = '' }) {
  return (
    <motion.div
      className={`absolute h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent ${className}`}
      style={{ width: '100%' }}
      animate={{
        opacity: [0, 1, 0],
        scaleX: [0, 1, 0]
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    />
  );
}

// Glowing Border Card
export function GlowingCard({ children, className = '', disableHover = false }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  const handleMouseMove = (e) => {
    if (disableHover || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={disableHover ? undefined : handleMouseMove}
      className={`relative overflow-hidden ${className}`}
      style={{
        background: '#0D1224',
        border: '1px solid rgba(99, 130, 255, 0.12)',
        borderRadius: '16px'
      }}
    >
      {!disableHover && (
        <div
          className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: `radial-gradient(350px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.15), transparent 50%)`
          }}
        />
      )}
      {children}
    </motion.div>
  );
}

// Scroll-driven 3D tilt/rotation on reveal
export function ScrollReveal3D({ children, className = '' }) {
  const ref = useRef(null);
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, rotateX: 12 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformStyle: 'preserve-3d', perspective: 1000 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Scroll-linked parallax section (moves at different speed to normal page scroll)
export function ScrollParallaxSection({ children, className = '', yOffset = 40 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [-yOffset, yOffset]);
  const ySmooth = useSpring(y, { stiffness: 100, damping: 20 });
  
  return (
    <motion.div ref={ref} style={{ y: ySmooth }} className={className}>
      {children}
    </motion.div>
  );
}

// Scroll-reactive 3D floating orb with gradient
export function FloatingOrb3D({ className = '', size = 250 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const ySmooth = useSpring(y, { stiffness: 80, damping: 20 });
  const rotateSmooth = useSpring(rotate, { stiffness: 80, damping: 20 });
  
  return (
    <motion.div
      ref={ref}
      className={`absolute rounded-full pointer-events-none ${className}`}
      style={{
        width: size,
        height: size,
        background: 'radial-gradient(circle at 30% 30%, #3B82F6 0%, #1D4ED8 35%, #06B6D4 70%, #080B14 100%)',
        boxShadow: '0 0 50px rgba(59, 130, 246, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.15)',
        rotate: rotateSmooth,
        y: ySmooth
      }}
    />
  );
}
